import {
  ArrowElement,
  Color,
  Element,
  LinePathBatchElement,
  NgonBatchElement,
  NO_INTERACTIVITY,
} from '@thive/canvas';
import { CubicBezierCurve, Matrix4, Vector2, Vector3 } from 'three';
import { Point, PointOfInterest, PoiConnection, Polygon, Track } from '@thive/fddm-types';

import { FDDM_TAG, combinedMatrix } from '@/modules/floorplanDesign/constants';
import {
  MAX_LINE_PATH_DIVISIONS,
  MIN_POI_CONNECTION_LENGTH,
  POI_CONNECTION_OFFSET,
  POI_CONNECTION_SCALE,
  TRACK_PIECE_COLOR,
  TRACK_PIECE_SIZE,
  VEHICLE_LAYOUT_POI_CONNECTION_COLOR,
  Z_INDEX_MAP,
} from '@/modules/canvas/constants/elements';
import { NumUtils } from '@/modules/common/utils';

const buildLinearTrackPieceId = (poiConnectionId: string, trackPieceIndex: number): string =>
  `track-${poiConnectionId}-${trackPieceIndex}`;

const buildCubicTrackPieceIds = (
  poiConnectionId: string,
  numberOfControlPoints: number,
  trackPieceIndex: number,
): string[] => {
  const numberOfBeziers = numberOfControlPoints - 3;

  return Array.from(
    { length: numberOfBeziers },
    (_, bezierIndex) => `track-${poiConnectionId}-${trackPieceIndex}-${bezierIndex}`,
  );
};

export const getTrackIds = (track: Track): string[] =>
  track.trackPieces.flatMap((trackPiece, trackPieceIndex) => {
    if (trackPiece.trackPieceType === 'LINEAR') {
      return [buildLinearTrackPieceId(track.poiConnectionId, trackPieceIndex)];
    }

    if (trackPiece.trackPieceType === 'CUBS') {
      return buildCubicTrackPieceIds(
        track.poiConnectionId,
        trackPiece.controlPoints.length,
        trackPieceIndex,
      );
    }

    return [];
  });

type BezierControlPoints = [Vector2, Vector2, Vector2, Vector2];
type TrackPiece = Track['trackPieces'][number];

const cubsToBezier = (controlPoints: [Point, Point, ...Point[]]): BezierControlPoints[] => {
  const beziers: BezierControlPoints[] = [];

  for (let i = 0; i < controlPoints.length - 3; i++) {
    const segment = controlPoints.slice(i, i + 4);

    const pointMatrix = new Matrix4(
      segment[0].x,
      segment[0].y,
      0,
      0,
      segment[1].x,
      segment[1].y,
      0,
      0,
      segment[2].x,
      segment[2].y,
      0,
      0,
      segment[3].x,
      segment[3].y,
      0,
      0,
    );

    const resultMatrix = new Matrix4();
    resultMatrix.multiplyMatrices(combinedMatrix, pointMatrix);

    beziers.push([
      new Vector2(resultMatrix.elements[0], resultMatrix.elements[4]),
      new Vector2(resultMatrix.elements[1], resultMatrix.elements[5]),
      new Vector2(resultMatrix.elements[2], resultMatrix.elements[6]),
      new Vector2(resultMatrix.elements[3], resultMatrix.elements[7]),
    ]);
  }

  return beziers;
};

const createNgonBatchElement = (
  id: string,
  polygons: Polygon[],
  visible = true,
): NgonBatchElement => ({
  type: 'NgonBatch',
  name: id,
  id,
  position: new Vector3(0, 0, Z_INDEX_MAP[FDDM_TAG.SWEPT_PATH]),
  rotation: new Vector3(0, 0, 0),
  fill: Color.fromRGBA(241, 169, 44, 100),
  ngons: polygons.map((polygon) =>
    polygon.points.map(
      (point) =>
        new Vector2(NumUtils.metersToMmRounded(point.x), NumUtils.metersToMmRounded(point.y)),
    ),
  ),
  interactivity: NO_INTERACTIVITY,
  tags: [FDDM_TAG.SWEPT_PATH],
  visible,
});

const createLinePathBatchElement = (
  id: string,
  lines: LinePathBatchElement['lines'],
  visible = true,
): LinePathBatchElement => ({
  type: 'LinePathBatch',
  name: id,
  id,
  lines,
  strokeWidth: TRACK_PIECE_SIZE,
  stroke: Color.fromHex(TRACK_PIECE_COLOR),
  interactivity: NO_INTERACTIVITY,
  tags: [FDDM_TAG.TRACK_PIECE],
  visible,
});

const createLinearTrackPiece = (trackPiece: TrackPiece): Vector3[][] => {
  const pointA = trackPiece.controlPoints[0];
  const pointB = trackPiece.controlPoints[1];
  if (!pointA || !pointB) return [];

  return [
    [
      new Vector3(
        NumUtils.metersToMmRounded(pointA.x),
        NumUtils.metersToMmRounded(pointA.y),
        Z_INDEX_MAP[FDDM_TAG.TRACK_PIECE],
      ),
      new Vector3(
        NumUtils.metersToMmRounded(pointB.x),
        NumUtils.metersToMmRounded(pointB.y),
        Z_INDEX_MAP[FDDM_TAG.TRACK_PIECE],
      ),
    ],
  ];
};

const createCubicTrackPieces = (trackPiece: TrackPiece): Vector3[][] =>
  cubsToBezier(trackPiece.controlPoints).map((bezier) => {
    const canvasBezier: BezierControlPoints = [
      new Vector2(NumUtils.metersToMmRounded(bezier[0].x), NumUtils.metersToMmRounded(bezier[0].y)),
      new Vector2(NumUtils.metersToMmRounded(bezier[1].x), NumUtils.metersToMmRounded(bezier[1].y)),
      new Vector2(NumUtils.metersToMmRounded(bezier[2].x), NumUtils.metersToMmRounded(bezier[2].y)),
      new Vector2(NumUtils.metersToMmRounded(bezier[3].x), NumUtils.metersToMmRounded(bezier[3].y)),
    ];
    const curve = new CubicBezierCurve(...canvasBezier);

    return curve
      .getPoints(MAX_LINE_PATH_DIVISIONS)
      .map((point) => new Vector3(point.x, point.y, Z_INDEX_MAP[FDDM_TAG.TRACK_PIECE]));
  });

const createTrackPieceLines = (trackPiece: TrackPiece): Vector3[][] => {
  if (trackPiece.trackPieceType === 'LINEAR') {
    return createLinearTrackPiece(trackPiece);
  }

  if (trackPiece.trackPieceType === 'CUBS') {
    return createCubicTrackPieces(trackPiece);
  }

  return [];
};

const getTrackBatchId = (baseId: string, batchId?: string): string => {
  if (!batchId) return baseId;

  return `${baseId}-${batchId}`;
};

const getTrackPieceGeometryKey = (trackPiece: TrackPiece): string => {
  return [
    trackPiece.controlPoints
      .map(
        (point) => `${NumUtils.metersToMmRounded(point.x)}:${NumUtils.metersToMmRounded(point.y)}`,
      )
      .join(':'),
    trackPiece.sweptPath.points
      .map(
        (point) => `${NumUtils.metersToMmRounded(point.x)}:${NumUtils.metersToMmRounded(point.y)}`,
      )
      .join(':'),
  ].join(':');
};

export const trackMapper = (
  tracks: Track[],
  tracksVisible?: boolean,
  sweptPathsVisible?: boolean,
  batchId?: string,
): Element[] => {
  const trackElementsVisible = tracksVisible ?? true;
  const sweptPathElementsVisible = sweptPathsVisible ?? trackElementsVisible;
  const allTrackPieces = tracks.flatMap((track) => track.trackPieces);

  const geometryKeys = new Set<string>();
  const uniqueTrackPieces = allTrackPieces.filter((trackPiece) => {
    const geometryKey = getTrackPieceGeometryKey(trackPiece);
    if (geometryKeys.has(geometryKey)) return false;
    geometryKeys.add(geometryKey);
    return true;
  });

  const trackPieces = uniqueTrackPieces.flatMap(createTrackPieceLines);
  const sweptPaths = uniqueTrackPieces.flatMap((trackPiece) =>
    trackPiece.sweptPath ? [trackPiece.sweptPath] : [],
  );

  const linePathBatchElement = createLinePathBatchElement(
    getTrackBatchId('track-batch', batchId),
    trackPieces,
    trackElementsVisible,
  );
  const ngonBatchElement = createNgonBatchElement(
    getTrackBatchId('track-swept-path-batch', batchId),
    sweptPaths,
    sweptPathElementsVisible,
  );

  return [linePathBatchElement, ngonBatchElement];
};

const createPoiConnectionElement = (
  id: string,
  pointA: Vector2,
  pointB: Vector2,
  offset: number,
  visible = true,
): ArrowElement | null => {
  const pointACanvas = pointA.clone().multiplyScalar(POI_CONNECTION_SCALE);
  const pointBCanvas = pointB.clone().multiplyScalar(POI_CONNECTION_SCALE);

  const aToB = pointBCanvas.clone().sub(pointACanvas);
  const distance = aToB.length();

  if (distance <= MIN_POI_CONNECTION_LENGTH) return null;

  const direction = aToB.clone().normalize();
  const safeOffset = Math.min(offset, Math.max(0, distance / 2 - MIN_POI_CONNECTION_LENGTH));

  const pointAContracted = pointACanvas.clone().add(direction.clone().multiplyScalar(safeOffset));
  const pointBContracted = pointBCanvas.clone().sub(direction.clone().multiplyScalar(safeOffset));

  return {
    type: 'Arrow',
    name: id,
    id,
    pointA: new Vector3(
      pointAContracted.x,
      pointAContracted.y,
      Z_INDEX_MAP[FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION],
    ),
    pointB: new Vector3(
      pointBContracted.x,
      pointBContracted.y,
      Z_INDEX_MAP[FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION],
    ),
    strokeWidth: 15,
    stroke: Color.fromHex(VEHICLE_LAYOUT_POI_CONNECTION_COLOR),
    interactivity: NO_INTERACTIVITY,
    tags: [FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION],
    visible,
  };
};

const getPoiConnectionPositionKey = (startPos: Vector2, endPos: Vector2): string => {
  return `${NumUtils.metersToMmRounded(startPos.x)}:${NumUtils.metersToMmRounded(
    startPos.y,
  )}:${NumUtils.metersToMmRounded(endPos.x)}:${NumUtils.metersToMmRounded(endPos.y)}`;
};

export const vehicleLayoutPoiConnectionMapper = (
  poiConnections: PoiConnection[],
  pois: PointOfInterest[],
  visible = true,
): Element[] => {
  const poisById = new Map(pois.map((poi) => [poi.id, poi]));
  const geometryKeys = new Set<string>();

  return poiConnections.flatMap((connection) => {
    const startPoi = poisById.get(connection.startPoiId);
    const endPoi = poisById.get(connection.endPoiId);

    if (!startPoi || !endPoi) return [];

    const startPos = new Vector2(startPoi.pose.x, startPoi.pose.y);
    const endPos = new Vector2(endPoi.pose.x, endPoi.pose.y);
    const positionKey = getPoiConnectionPositionKey(startPos, endPos);

    if (geometryKeys.has(positionKey)) return [];

    geometryKeys.add(positionKey);
    const element = createPoiConnectionElement(
      connection.id,
      startPos,
      endPos,
      POI_CONNECTION_OFFSET,
      visible,
    );

    if (!element) return [];

    return [element];
  });
};
